import { Form, useFormikContext } from "formik"

import { FilterValues } from "."
import { JobDrawerTypes } from "./JobDrawer"
import JobRow from "./JobRow"

import LoaderV2 from "@components/elements/LoaderV2"
import TextButton from "@components/elements/V2/Buttons/TextButton"
import SearchIcon from "@components/elements/V2/Icons/SearchIcon"
import DropdownInput, {
  SelectOption,
} from "@components/elements/V2/Inputs/DropdownInput"
import TextInput from "@components/elements/V2/Inputs/TextInput"
import Typography from "@components/elements/V2/Typography"
import { Color } from "@constants/V2/color"
import React, { useMemo } from "react"

type InnerProps = {
  isLoading: boolean
  error: string | null
  records: HiBob.JobAd[]
  locations: SelectOption[]
  departments: SelectOption[]
  employmentTypes: SelectOption[]
  drawerData: JobDrawerTypes
}

const JobListingsInner = ({
  isLoading,
  error,
  records,
  locations,
  departments,
  employmentTypes,
  drawerData,
}: InnerProps) => {
  const { values, resetForm } = useFormikContext<FilterValues>()

  const filtered = useMemo(() => {
    const search = values.search.toLowerCase()
    return records.filter((r) => {
      if (search && !r.title.toLowerCase().includes(search)) return false
      if (values.location && r.site !== values.location) return false
      if (values.department && r.department !== values.department) return false
      if (values.type && r.employmentType !== values.type) return false
      return true
    })
  }, [records, values])

  const hasNoRoles = Boolean(error) || filtered.length === 0

  return (
    <Form>
      <div className="flex flex-col gap-24">
        <div className="flex flex-col gap-20 sm:flex-row sm:flex-wrap">
          <TextInput
            id="search"
            name="search"
            type="text"
            placeholder="Search roles, e.g. engineer"
            icon={<SearchIcon className="h-20 w-20 text-charcoal/40" />}
            disabled={isLoading}
            className="sm:flex-[2]"
            isRounded
          />
          <DropdownInput
            id="location"
            name="location"
            placeholder="Location"
            options={locations}
            disabled={isLoading}
            className="sm:flex-1"
            isRounded
          />
          <DropdownInput
            id="department"
            name="department"
            placeholder="Department"
            options={departments}
            disabled={isLoading}
            className="sm:flex-1"
            isRounded
          />
          <DropdownInput
            id="type"
            name="type"
            placeholder="Type"
            options={employmentTypes}
            disabled={isLoading}
            className="sm:flex-1"
            isRounded
          />
          <TextButton
            text="Clear"
            style="text"
            color={Color.Blue}
            onClick={() => resetForm()}
            disabled={isLoading}
            className="self-center sm:ml-auto"
          />
        </div>

        <div className="-mx-16 my-10 w-[calc(100%+32px)] border-b border-gray" />

        {isLoading ? (
          <div className="mt-10 flex items-center justify-center">
            <LoaderV2 text="Searching..." />
          </div>
        ) : hasNoRoles ? (
          <Typography
            className="py-56 text-center"
            size="body-lg"
            font="grotesk"
            weight="medium"
            color={Color.Charcoal}
            text="No available open roles"
          />
        ) : (
          <>
            <Typography
              size="body-md"
              font="grotesk"
              weight="book"
              color={Color.Coal}
              text={`Showing ${filtered.length} ${filtered.length === 1 ? "role" : "roles"}`}
              highlightedText={String(filtered.length)}
              highlightWeight="medium"
              highlightColor={Color.Charcoal}
            />

            <div className="pb-24">
              {filtered.map((job) => (
                <JobRow key={job.id} job={job} drawerData={drawerData} />
              ))}
            </div>
          </>
        )}
      </div>
    </Form>
  )
}

export default JobListingsInner
